/**
 * Calibration Queue — staff-only index of TRDAAS calibration jobs.
 *
 * Shows jobs awaiting human review first (runpod_status='calibration_review'),
 * then recently processed jobs that have calibration data.  Links to the
 * review page at /games/<job_id>/calibration, React since the port.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api, type Session } from '../lib/api'
import { ClubLogo } from '../components/ClubLogo'
import './calibration-queue.css'

type Job = {
  id: string
  game_code: string
  org_id: number
  org_code: string | null
  org_name: string | null
  org_logo_url: string | null
  status: string | null
  created_at: string | null
  match_date: string | null
}
type Payload = {
  pending: Job[]
  recent: Job[]
}

export default function CalibrationQueue({ session: _session }: { session: Session }) {
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    api<Payload>('/api/v1/calibration/queue')
      .then(setData)
      .catch((e) => setError(String(e)))
  }, [])

  useEffect(load, [load])

  if (error) return <div className="panel cq-page"><p className="adm-note" role="alert">{error}</p></div>
  if (!data) return <div className="panel cq-page"><p className="muted">Loading…</p></div>

  return (
    <div className="panel cq-page">
      <h2 className="cq-title">Calibration Queue</h2>
      <p className="muted">TRDAAS calibration jobs awaiting human review, plus recently processed.</p>

      <Section title={`Awaiting Review (${data.pending.length})`}
               jobs={data.pending} badge="review" />
      <Section title={`Recently Processed (${data.recent.length})`}
               jobs={data.recent} badge="status" />
    </div>
  )
}

function Section({ title, jobs, badge }: {
  title: string; jobs: Job[]; badge: 'review' | 'status'
}) {
  return (
    <div className="cq-section">
      <h3 className="cq-section__title">{title}</h3>
      {jobs.length === 0
        ? <p className="muted">None.</p>
        : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>Game</th>
                <th>Club</th>
                <th>Status</th>
                <th>Uploaded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td className="cq-mono">{j.game_code}</td>
                  <td>
                    <span className="cq-org">
                      <ClubLogo code={j.org_code ?? '?'}
                                url={j.org_logo_url} size="sm" />
                      {j.org_name ?? j.org_code ?? '—'}
                    </span>
                  </td>
                  <td><Badge status={j.status} mode={badge} /></td>
                  <td className="muted">
                    {j.created_at ? j.created_at.slice(0, 16).replace('T', ' ') : '—'}
                  </td>
                  <td>
                    <Link className="cq-link" to={`/games/${j.id}/calibration`}>
                      {badge === 'review' ? 'Review' : 'View'} &rarr;
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </div>
  )
}

function Badge({ status, mode }: { status: string | null; mode: 'review' | 'status' }) {
  if (mode === 'review') {
    return <span className="cq-badge cq-badge--review">Awaiting Review</span>
  }
  const cls = status === 'completed' ? 'cq-badge--completed'
    : status === 'failed' ? 'cq-badge--failed'
    : 'cq-badge--other'
  return <span className={`cq-badge ${cls}`}>{status ?? '—'}</span>
}
